// Generatore del codice da incollare nel sito del ristorante.
import i18n from "i18n";

// Le opzioni corrispondono agli attributi data-* di widget.js (backend: wa_app/utils/widget.js).

// Icone (stesso disegno del widget): tracciati SVG 24x24.
export const ICONS = {
  wine: "M8 2h8l1 7a5 5 0 0 1-10 0zM12 14v7M8 21h8",
  menu: "M5 3v6a2 2 0 0 0 4 0V3M7 11v10M16 21V3c-2 1-3.5 4-3.5 8H16",
  beer: "M5 8h10v12H5zM15 10h2a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-2M5 8c0-2 2-3 3-3 1 0 1 1 2 1s2-1 3-1 2 1 2 3",
  cocktail: "M3 4h18l-9 9zM12 13v8M8 21h8",
  spirits: "M6 4h12l-1.5 16h-9zM7 10h10",
  calendar: "M4 6h16v14H4zM4 10h16M8 3v4M16 3v4",
  cart: "M3 4h2l2.5 11h10L20 7H6M9 20h.01M17 20h.01",
};

// Icone selezionabili (la prima e' "nessuna"); il nome mostrato si traduce al momento dell'uso.
export const ICON_KEYS = ["", "wine", "menu", "beer", "cocktail", "spirits", "calendar", "cart"];
export const iconLabel = (key) => i18n.t(`cards:embed.icons.${key || "none"}`);

// Punti di partenza: testo e icona gia' pronti per ogni tipo di carta.
// name e label sono letti al momento dell'uso, cosi' seguono la lingua scelta.
const preset = (key, icon) => ({
  icon,
  get name() { return i18n.t(`cards:embed.kinds.${key}`); },
  get label() { return i18n.t(`cards:embed.presetLabels.${key}`); },
});
export const PRESETS = {
  wine: preset("wine", "wine"),
  menu: preset("menu", "menu"),
  beer: preset("beer", "beer"),
  spirits: preset("spirits", "spirits"),
  cocktail: preset("cocktail", "cocktail"),
  booking: preset("booking", "calendar"),
  order: preset("order", "cart"),
};

// Dal nome di una carta ("Carta vini sala", "Birre artigianali"...) al tipo piu' probabile.
export const guessKind = (name = "") => {
  const n = name.toLowerCase();
  if (/vin|wine|cantina|enoteca/.test(n)) return "wine";
  if (/birr|beer|bevand/.test(n)) return "beer";
  if (/cocktail|drink|mixolog/.test(n)) return "cocktail";
  if (/distillat|spirit|amar|grapp|whisk|gin|rum|liquor/.test(n)) return "spirits";
  return "menu";
};

export const DEFAULTS = {
  label: "",
  icon: "",
  color: "#434448",
  textColor: "#ffffff",
  style: "solid", // solid | outline
  size: "medium", // small | medium | large
  radius: "8", // "pill" o pixel
  where: "inline", // inline | right | left | center | trigger | page
  open: "modal", // modal | tab
  fullWidth: false,
  trigger: "",
};

export const presetOptions = (kind) => {
  const p = PRESETS[kind] || PRESETS.menu;
  return { ...DEFAULTS, label: p.label, icon: p.icon };
};

const esc = (v) =>
  String(v).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const isHex = (v) => /^#[0-9a-f]{6}$/i.test(v || "");

// Solo gli attributi diversi dal comportamento di default del widget, cosi' il codice resta corto.
export const buildSnippet = (url, hostId, o) => {
  const opt = { ...DEFAULTS, ...o };
  const attrs = [];
  const add = (name, value) => attrs.push(`data-${name}="${esc(value)}"`);
  if (opt.where === "page") {
    return `<div id="${hostId}"></div>\n<script src="${url}" async></script>`;
  }
  if (opt.where === "trigger") {
    add("trigger", opt.trigger.trim() || "#il-tuo-pulsante");
    if (opt.open === "tab") add("open", "tab");
    return `<script src="${url}" ${attrs.join(" ")} async></script>`;
  }
  if (opt.where === "inline") add("mode", "inline");
  if (opt.where === "left" || opt.where === "center") add("position", opt.where);
  if (opt.label.trim()) add("label", opt.label.trim());
  if (isHex(opt.color) && opt.color.toLowerCase() !== "#1d1b17") add("color", opt.color);
  if (opt.style === "outline") add("style", "outline");
  else if (isHex(opt.textColor) && opt.textColor.toLowerCase() !== "#ffffff") add("text-color", opt.textColor);
  if (opt.icon) add("icon", opt.icon);
  if (opt.size !== "medium") add("size", opt.size);
  const defaultRadius = opt.where === "inline" ? "8" : "pill";
  if (String(opt.radius) !== defaultRadius) add("radius", opt.radius);
  if (opt.where === "inline" && opt.fullWidth) add("width", "full");
  if (opt.open === "tab") add("open", "tab");
  return `<script src="${url}" ${attrs.join(" ")} async></script>`;
};

// Codice senza JavaScript: un semplice link, per siti che non permettono script.
export const buildLink = (pageUrl, o) => {
  const opt = { ...DEFAULTS, ...o };
  const bg = isHex(opt.color) ? opt.color : DEFAULTS.color;
  const fg = isHex(opt.textColor) ? opt.textColor : DEFAULTS.textColor;
  const look = opt.style === "outline"
    ? `border:2px solid ${bg};color:${bg}`
    : `background:${bg};color:${fg}`;
  return `<a href="${pageUrl}" target="_blank" rel="noopener" style="display:inline-block;padding:12px 20px;border-radius:8px;font:600 16px system-ui,sans-serif;text-decoration:none;${look}">${esc(opt.label.trim() || "Apri")}</a>`;
};

export const buildIframe = (pageUrl, label) =>
  `<iframe src="${pageUrl}" title="${esc(label || "Carta")}" style="border:0;width:100%;height:800px" loading="lazy" allow="payment"></iframe>`;

// Rapporto di contrasto tra due colori (WCAG): sotto 4.5 il testo si legge male.
const channel = (c) => {
  const v = c / 255;
  return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
};
const luminance = (hex) => {
  const n = parseInt(hex.slice(1), 16);
  return 0.2126 * channel(n >> 16) + 0.7152 * channel((n >> 8) & 255) + 0.0722 * channel(n & 255);
};
export const contrastRatio = (a, b) => {
  if (!isHex(a) || !isHex(b)) return null;
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
};

// Contrasto che conta davvero: testo su sfondo (pieno) oppure colore su pagina bianca (contorno).
export const readableContrast = (o) => {
  const opt = { ...DEFAULTS, ...o };
  return opt.style === "outline" ? contrastRatio(opt.color, "#ffffff") : contrastRatio(opt.color, opt.textColor);
};

// Stile del bottone nell'anteprima (stessi valori del widget).
export const previewStyle = (o) => {
  const opt = { ...DEFAULTS, ...o };
  const pad = { small: "8px 14px", medium: "12px 20px", large: "16px 28px" }[opt.size] || "12px 20px";
  const fs = { small: 14, medium: 16, large: 18 }[opt.size] || 16;
  const radius = opt.radius === "pill" ? "999px" : `${Number(opt.radius) || 0}px`;
  const bg = isHex(opt.color) ? opt.color : DEFAULTS.color;
  const fg = isHex(opt.textColor) ? opt.textColor : DEFAULTS.textColor;
  return {
    display: opt.fullWidth && opt.where === "inline" ? "flex" : "inline-flex",
    width: opt.fullWidth && opt.where === "inline" ? "100%" : undefined,
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    padding: pad,
    fontSize: fs,
    fontWeight: 600,
    lineHeight: 1.2,
    borderRadius: radius,
    cursor: "default",
    ...(opt.style === "outline"
      ? { background: "transparent", color: bg, border: `2px solid ${bg}` }
      : { background: bg, color: fg, border: 0 }),
  };
};
